import { BarChartOutlined, ExploreOutlined, RouteOutlined } from '@mui/icons-material';
import { Box, Chip, styled, Typography } from '@mui/material';
import React, { useCallback } from 'react';

import intellioInsightsImage from '../assets/wm_intellio_insights.jpg';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import {
  apqcDataMetrics,
  capabilitiesData,
  capabilitiesDataBpo,
  capabilitiesDataCape,
  clientStrategyData,
  industryData,
  industryDataMetrics,
} from '../constants/insightsConstants';

const StyledLink = styled('a')({
  color: '#0047FF',
  textDecoration: 'none',
  ':hover': {
    textDecoration: 'underline',
  },
});

const BulletList = styled('ul')({
  listStyleType: 'disc',
  paddingLeft: '20px',
  margin: 0,
});

const BulletItem = styled('li')({
  marginBottom: '6px',
});

const StyledInsightCard = styled(Box)({
  width: '320px',
  minHeight: '420px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  boxShadow: '0px 4px 4px rgba(0,0,0,0.1)',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
});

const CardHeader = styled(Box)({
  backgroundColor: '#0b0b5f',
  color: '#ffffff',
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const CardContent = styled(Box)({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const CardsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  justifyContent: 'center',
  padding: '20px',
});

// Constants for repeated styles
const HEADER_FONT_SIZE = '18px';
const CONTENT_FONT_SIZE = '16px';
const FONT_WEIGHT_BOLD = 600;

interface CardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const InsightCardComponent: React.FC<CardProps> = ({ icon, title, children }) => (
  <StyledInsightCard>
    <CardHeader>
      {icon}
      <Typography sx={{ fontSize: HEADER_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD, color: '#fff' }}>
        {title}
      </Typography>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </StyledInsightCard>
);

const IntellioInsight: React.FC = () => {
  const analytics = useAnalytics();

  const handleLinkClick = useCallback(
    (item: { id: string; label: string; href: string }, category: string) => {
      analytics.event(
        AnalyticsEvent.WM_INTELLIO_INSIGHTS_LINK_CLICK,
        { id: item.id },
        {
          linkName: item.label,
          linkUrl: item.href,
          cardLocation: category,
        }
      );
    },
    [analytics]
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px', mt: 2 }}>
        <Box sx={{ width: '100%', maxWidth: '1040px' }}>
          <img
            src={intellioInsightsImage}
            alt="Intellio Insights"
            style={{ maxWidth: '200px', height: 'auto' }}
          />
          <Typography
            sx={{
              mt: 2,
              fontSize: '12px',
              color: '#0b0b5f',
            }}
          >
            <Box component="span" sx={{ fontWeight: FONT_WEIGHT_BOLD }}>
              ANNOUNCEMENT:
            </Box>{' '}
            The Computer Economics data has been retired and replaced with APQC metrics which can be
            accessed below on the APQC dashboard. (2.13.25)
          </Typography>
        </Box>
      </Box>

      <CardsContainer>
        <InsightCardComponent
          icon={<BarChartOutlined sx={{ color: '#fff', width: 20, height: 20 }} />}
          title="Industry & Third Party Data"
        >
          <BulletList sx={{ listStyleType: 'none', paddingLeft: 0 }}>
            {apqcDataMetrics.map((item) => (
              <BulletItem key={item.label}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledLink
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleLinkClick(item, 'Industry & Third Party Data')}
                  >
                    <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
                      {item.label}
                    </Typography>
                  </StyledLink>
                  {item.isNew && (
                    <Chip
                      label="New"
                      size="small"
                      sx={{
                        bgcolor: '#ff2bbf',
                        color: '#fff',
                        height: '20px',
                        fontSize: '12px',
                      }}
                    />
                  )}
                </Box>
              </BulletItem>
            ))}
          </BulletList>
          <BulletList sx={{ listStyleType: 'none', paddingLeft: 0 }}>
            {industryDataMetrics.map((item) => (
              <BulletItem key={item.label}>
                <StyledLink
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleLinkClick(item, 'Industry & Third Party Data')}
                >
                  <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
                    {item.label}
                  </Typography>
                </StyledLink>
                <BulletList sx={{ listStyleType: 'disc', paddingLeft: '20px', mt: 1 }}>
                  {industryData.map((subItem) => (
                    <BulletItem key={subItem.label}>
                      <StyledLink
                        href={subItem.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleLinkClick(subItem, 'Industry & Third Party Data')}
                      >
                        <Typography sx={{ fontSize: CONTENT_FONT_SIZE }}>
                          {subItem.label}
                        </Typography>
                      </StyledLink>
                    </BulletItem>
                  ))}
                </BulletList>
              </BulletItem>
            ))}
          </BulletList>
        </InsightCardComponent>
        <InsightCardComponent
          icon={<ExploreOutlined sx={{ color: '#fff', width: 20, height: 20 }} />}
          title="Capabilities & Offerings"
        >
          <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
            Transaction Advisory
          </Typography>

          <BulletList>
            {capabilitiesData.map((item) => (
              <BulletItem key={item.label}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledLink
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      handleLinkClick(item, 'Capabilities & Offerings - Transaction Advisory')
                    }
                  >
                    <Typography sx={{ fontSize: CONTENT_FONT_SIZE }}>{item.label}</Typography>
                  </StyledLink>

                  {item.isNew && (
                    <Chip
                      label="New"
                      size="small"
                      sx={{
                        bgcolor: '#ff2bbf',
                        color: '#fff',
                        height: '20px',
                        fontSize: '12px',
                      }}
                    />
                  )}
                </Box>
              </BulletItem>
            ))}
          </BulletList>
          <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
            CAPE
            <BulletList>
              {capabilitiesDataCape.map((item) => (
                <BulletItem key={item.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StyledLink
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleLinkClick(item, 'Capabilities & Offerings - CAPE')}
                    >
                      <Typography sx={{ fontSize: CONTENT_FONT_SIZE }}>{item.label}</Typography>
                    </StyledLink>

                    {item.isNew && (
                      <Chip
                        label="New"
                        size="small"
                        sx={{
                          bgcolor: '#ff2bbf',
                          color: '#fff',
                          height: '20px',
                          fontSize: '12px',
                        }}
                      />
                    )}
                  </Box>
                </BulletItem>
              ))}
            </BulletList>
          </Typography>
          <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
            ITS BPO
            <BulletList>
              {capabilitiesDataBpo.map((item) => (
                <BulletItem key={item.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StyledLink
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleLinkClick(item, 'Capabilities & Offerings - ITS BPO')}
                    >
                      <Typography sx={{ fontSize: CONTENT_FONT_SIZE }}>{item.label}</Typography>
                    </StyledLink>

                    {item.isNew && (
                      <Chip
                        label="New"
                        size="small"
                        sx={{
                          bgcolor: '#ff2bbf',
                          color: '#fff',
                          height: '20px',
                          fontSize: '12px',
                        }}
                      />
                    )}
                  </Box>
                </BulletItem>
              ))}
            </BulletList>
          </Typography>
        </InsightCardComponent>
        <InsightCardComponent
          icon={<RouteOutlined sx={{ color: '#fff', width: 20, height: 20 }} />}
          title="Client Strategy"
        >
          <BulletList sx={{ listStyleType: 'none', paddingLeft: 0 }}>
            {clientStrategyData.map((item) => (
              <BulletItem key={item.label}>
                <StyledLink
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleLinkClick(item, 'Client Strategy')}
                >
                  <Typography sx={{ fontSize: CONTENT_FONT_SIZE, fontWeight: FONT_WEIGHT_BOLD }}>
                    {item.label}
                  </Typography>
                </StyledLink>
              </BulletItem>
            ))}
          </BulletList>
        </InsightCardComponent>
      </CardsContainer>
    </Box>
  );
};

export default IntellioInsight;
